import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InputText,
  Modal,
  Select,
  Spinner,
  Table,
} from '../../components'
import type { TableColumn } from '../../components'
import { getErrorCode } from '../../lib/errorCode'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import {
  AdminPageHeader,
  ReadOnlyNote,
  adminStyles as styles,
  changedFields,
  formatGrams,
  hasFields,
  numberText,
  parseNonNegative,
} from '../admin/AdminPage'
import { PLANNING_GROUPS, PLANNING_GROUP_OPTIONS, isPlanningGroup } from './planning'
import type { PlanningGroup } from './planning'
import { useConfigurationRefresh } from './useConfigurationRefresh'

type PlanMachine = RouterOutputs['configuration']['listPlanMachines'][number]

/** The API's cap on `name` (`configuration.trpc.ts`). */
const NAME_MAX_LENGTH = 60

const FAMILY_NOTE = 'El motor deduce qué cocina cada máquina por el principio de su nombre: '
  + '«Basket», «Vario» o «Fuego» para la familia de la olla, y «Marmita Caldos» para los caldos. '
  + 'Cambiar el nombre puede cambiar lo que el plan le asigna.'

const planMachineErrorMessage = (error: unknown): string =>
  getErrorCode(error) === 'CONFLICT' ? 'Ya existe una máquina con ese nombre.' : trpcMessage(error)

/**
 * CAPACIDADES: the machines as the planner sees them, per planning group and in
 * the order the planner tries them. Logical machines of the engine, with no
 * code in the mirror — so no freshness here: this is WASP's own table.
 */
export const PlanMachinesPage = (): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const refreshStatus = useConfigurationRefresh()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<PlanMachine | null>(null)
  const [deleting, setDeleting] = useState<PlanMachine | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const machinesQuery = trpc.configuration.listPlanMachines.useQuery()

  const afterWrite = (): void => {
    setActionError(null)
    void utils.configuration.listPlanMachines.invalidate()
    refreshStatus()
  }

  const move = trpc.configuration.movePlanMachine.useMutation({
    onSuccess: afterWrite,
    onError: (error) => setActionError(trpcMessage(error)),
  })
  const remove = trpc.configuration.deletePlanMachine.useMutation({
    onSuccess: () => {
      setDeleting(null)
      afterWrite()
    },
    onError: (error) => {
      setDeleting(null)
      setActionError(trpcMessage(error))
    },
  })

  const columnsFor = (machines: Array<PlanMachine>): Array<TableColumn<PlanMachine>> => [
    { key: 'name', header: 'Máquina', render: (machine) => <strong>{machine.name}</strong> },
    {
      key: 'recipes',
      header: 'Cap. recetas',
      align: 'right',
      render: (machine) => <span className={styles['admin-page__number']}>{formatGrams(machine.maxCapacityRecipes)}</span>,
    },
    {
      key: 'broths',
      header: 'Cap. caldos',
      align: 'right',
      hideOnMobile: true,
      render: (machine) => <span className={styles['admin-page__number']}>{formatGrams(machine.maxCapacityBroths)}</span>,
    },
    {
      key: 'dead',
      header: 'Tiempo muerto',
      align: 'right',
      hideOnMobile: true,
      render: (machine) => <span className={styles['admin-page__number']}>{`${machine.deadTimeMinutes} min`}</span>,
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (machine) => {
        const index = machines.indexOf(machine)
        return (
          <div className={styles['admin-page__row-actions']}>
            <Button
              variant="secondary"
              disabled={isReadOnly || move.isPending || index === 0}
              onClick={() => move.mutate({ id: machine.id, direction: 'up' })}
              aria-label={`Subir ${machine.name}`}
              label="Subir"
            />
            <Button
              variant="secondary"
              disabled={isReadOnly || move.isPending || index === machines.length - 1}
              onClick={() => move.mutate({ id: machine.id, direction: 'down' })}
              aria-label={`Bajar ${machine.name}`}
              label="Bajar"
            />
            <Button
              variant="secondary"
              disabled={isReadOnly}
              onClick={() => setEditing(machine)}
              aria-label={`Editar ${machine.name}`}
              label="Editar"
            />
            <Button
              variant="danger"
              disabled={isReadOnly}
              onClick={() => setDeleting(machine)}
              aria-label={`Eliminar ${machine.name}`}
              label="Eliminar"
            />
          </div>
        )
      },
    },
  ]

  let body: ReactElement
  if (machinesQuery.isPending) {
    body = <Spinner label="Cargando máquinas del plan…" />
  } else if (machinesQuery.isError) {
    body = <ErrorState error={machinesQuery.error} onRetry={() => void machinesQuery.refetch()} />
  } else if (machinesQuery.data.length === 0) {
    body = (
      <EmptyState
        title="No hay máquinas del plan"
        message="Sin ellas el planificador no puede generar un plan."
        action={<Button disabled={isReadOnly} onClick={() => setCreating(true)} label="Añadir la primera" />}
      />
    )
  } else {
    const machines = machinesQuery.data
    body = (
      <>
        {PLANNING_GROUPS.map((group) => {
          const inGroup = machines
            .filter((machine) => machine.planningGroup === group)
            .sort((left, right) => left.position - right.position)
          if (inGroup.length === 0) {
            return null
          }
          return (
            <section key={group} className={styles['admin-page__section']}>
              <h2 className={styles['admin-page__section-title']}>{group}</h2>
              <Table columns={columnsFor(inGroup)} rows={inGroup} rowKey={(machine) => machine.id} caption={`Máquinas del grupo ${group}`} />
            </section>
          )
        })}
      </>
    )
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Máquinas del plan"
        intro="Las máquinas que el planificador reparte, por grupo y en el orden en que las prueba."
        actions={<Button disabled={isReadOnly} onClick={() => setCreating(true)} label="Nueva máquina" />}
      />
      {isReadOnly && <ReadOnlyNote />}
      <p className={`${styles['admin-page__note']} ${styles['admin-page__note--warn']}`}>{FAMILY_NOTE}</p>
      {actionError !== null && <p role="alert" className={styles['admin-page__error']}>{actionError}</p>}

      {body}

      {creating && (
        <PlanMachineDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            afterWrite()
          }}
        />
      )}
      {editing !== null && (
        <PlanMachineDialog
          machine={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            afterWrite()
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        variant="danger"
        title="Eliminar máquina del plan"
        message={deleting === null ? '' : `El planificador dejará de usar «${deleting.name}» desde el próximo plan.`}
        confirmLabel="Eliminar"
        isLoading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate({ id: deleting.id })
          }
        }}
      />
    </section>
  )
}

type PlanMachineDialogProps = {
  /** The machine being edited; absent to create one (it goes last in its group). */
  machine?: PlanMachine
  onClose: () => void
  onSaved: () => void
}

const PlanMachineDialog = ({ machine, onClose, onSaved }: PlanMachineDialogProps): ReactElement => {
  const [name, setName] = useState(machine?.name ?? '')
  const [planningGroup, setPlanningGroup] = useState<PlanningGroup | ''>(machine?.planningGroup ?? '')
  const [recipes, setRecipes] = useState(numberText(machine?.maxCapacityRecipes ?? null))
  const [broths, setBroths] = useState(numberText(machine?.maxCapacityBroths ?? null))
  const [deadTime, setDeadTime] = useState(machine === undefined ? '0' : String(machine.deadTimeMinutes))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onError = (error: unknown): void => setErrorMessage(planMachineErrorMessage(error))
  const create = trpc.configuration.createPlanMachine.useMutation({ onSuccess: onSaved, onError })
  const update = trpc.configuration.updatePlanMachine.useMutation({ onSuccess: onSaved, onError })
  const isPending = create.isPending || update.isPending

  const parsedRecipes = parseNonNegative(recipes)
  const parsedBroths = parseNonNegative(broths)
  const parsedDeadTime = parseNonNegative(deadTime, { integer: true })
  const numbersValid = parsedRecipes.valid && parsedBroths.valid && parsedDeadTime.valid && parsedDeadTime.value !== null
  const trimmedName = name.trim()
  const renamed = machine !== undefined && trimmedName !== machine.name

  const draft = numbersValid && planningGroup !== '' && trimmedName !== ''
    ? {
      name: trimmedName,
      planningGroup,
      maxCapacityRecipes: parsedRecipes.valid ? parsedRecipes.value : null,
      maxCapacityBroths: parsedBroths.valid ? parsedBroths.value : null,
      deadTimeMinutes: parsedDeadTime.valid ? parsedDeadTime.value ?? 0 : 0,
    }
    : null
  const patch = draft === null || machine === undefined
    ? {}
    : changedFields({
      name: machine.name,
      planningGroup: machine.planningGroup,
      maxCapacityRecipes: machine.maxCapacityRecipes,
      maxCapacityBroths: machine.maxCapacityBroths,
      deadTimeMinutes: machine.deadTimeMinutes,
    }, draft)
  const canSubmit = draft !== null && (machine === undefined || hasFields(patch))

  return (
    <Modal open title={machine === undefined ? 'Nueva máquina del plan' : `Editar ${machine.name}`} onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (draft === null) {
            return
          }
          setErrorMessage(null)
          if (machine === undefined) {
            create.mutate(draft)
          } else {
            update.mutate({ id: machine.id, ...patch })
          }
        }}
      >
        <InputText
          label="Nombre"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          hint="Empieza por la familia: «Basket 1», «Vario 2», «Fuego 3», «Marmita Caldos»…"
          onChange={(event) => setName(event.target.value)}
          required
        />
        {renamed && (
          <p className={`${styles['admin-page__note']} ${styles['admin-page__note--warn']}`}>
            {`Vas a renombrar «${machine.name}». ${FAMILY_NOTE}`}
          </p>
        )}
        <Select
          label="Grupo"
          placeholder="Elige un grupo…"
          options={PLANNING_GROUP_OPTIONS}
          value={planningGroup}
          onChange={(event) => {
            const { value } = event.target
            setPlanningGroup(isPlanningGroup(value) ? value : '')
          }}
        />
        <InputText
          label="Capacidad recetas (g)"
          inputMode="decimal"
          value={recipes}
          hint="Vacío = sin límite."
          onChange={(event) => setRecipes(event.target.value)}
        />
        <InputText
          label="Capacidad caldos (g)"
          inputMode="decimal"
          value={broths}
          hint="Vacío = sin límite."
          onChange={(event) => setBroths(event.target.value)}
        />
        <InputText
          label="Tiempo muerto (min)"
          inputMode="numeric"
          value={deadTime}
          hint="Limpieza y cambio entre preparaciones. 0 para Manual y Remojo."
          onChange={(event) => setDeadTime(event.target.value)}
          required
        />
        {!numbersValid && (
          <p role="alert" className={styles['admin-page__error']}>
            Las capacidades son gramos (0 o más) y el tiempo muerto, minutos enteros (0 o más).
          </p>
        )}
        {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={isPending} label="Cancelar" />
          <Button
            type="submit"
            disabled={isPending || !canSubmit}
            label={machine === undefined ? 'Crear máquina' : 'Guardar cambios'}
          />
        </div>
      </form>
    </Modal>
  )
}
